import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import { Box, Button, Grid, Modal, Typography, TextField, InputAdornment } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';

const CLIENT_VENDOR_RELATIONSHIPS = [
  { id: '2F9A6B11-3A0E-4D33-8B6C-7C0F7A6A2D8E', label: 'Client Required' },
  { id: '8C64C4E3-7A57-4E6A-9B7D-0E2A8A5A9B31', label: 'Client Preferred' },
  { id: 'D0D3B8C6-8E71-4A9D-9B1A-4D2E6B7C1F90', label: 'Client Do Not Use' },
  { id: '00000000-0000-0000-0000-000000000000', label: 'None' }
];

const styles = theme => ({
  cardContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  modalContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',

    backgroundColor: theme.palette.background.paper,
    opacity: 1,
    boxShadow: theme.shadows[24],
    borderRadius: 8,

    // Bigger sizing
    width: '92vw',
    maxWidth: 1400,
    height: '88vh',
    maxHeight: '88vh',

    // Layout/scrolling
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden' // container doesn't scroll; inner area does
  },

  // If you want the grid area to scroll while header/footer stay pinned:
  modalBody: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'auto'
  },
  gridHeaderCell: {
    padding: '8px'
  },
  vendorNameCell: {
    padding: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  selectableCell: {
    height: 40,
    border: '1px solid #e0e0e0',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  selectedDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2)
  }
});

const ClientVendorRelationshipGrid = props => {
  const {
    classes,
    clients = [],
    clientVendorRelationships = []
  } = props;


  const [modalOpen, setModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const [clientVendorRelationshipForm, setClientVendorRelationshipForm] = useState({});

  const relationshipLookupFromApi = useMemo(() => {
    const map = {};
    (clientVendorRelationships || []).forEach(r => {
      const clientKey = r?.clientId ?? r?.clientRecordId;
      if (clientKey != null) {
        map[String(clientKey)] = {
          relationshipId: (r.relationshipId || CLIENT_VENDOR_RELATIONSHIPS[3].id).toUpperCase(),
          clientDiscountRate: r.clientDiscountRate
        };
      }
    });
    return map;
  }, [clientVendorRelationships]);

  const normalizedClients = useMemo(() => {
    if (Array.isArray(clients)) {
      return clients;
    }

    if (clients && typeof clients === 'object') {
      return Object.values(clients);
    }

    return [];
  }, [clients]);

  const filteredClients = useMemo(() => {
    const list = normalizedClients;
    if (!searchText.trim()) return list;
    const s = searchText.toLowerCase().trim();
    return list.filter(c => (c.shortName || '').toLowerCase().includes(s));
  }, [normalizedClients, searchText]);

  const openModal = () => {
    setClientVendorRelationshipForm({ ...relationshipLookupFromApi });
    setSearchText('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSearchText('');
  };
  const relationshipCounts = useMemo(() => {
    const counts = {};
    CLIENT_VENDOR_RELATIONSHIPS.forEach(r => (counts[r.id] = 0));
    (clientVendorRelationships || []).forEach(r => {
      if (r?.relationshipId && counts[r.relationshipId.toUpperCase()] != null) {
        counts[r.relationshipId.toUpperCase()] += 1;
      }
    });

    if (normalizedClients.length > 0) {
      let vrCount = clientVendorRelationships ? clientVendorRelationships.length : 0;
      const noneCount = normalizedClients.length - vrCount;
      counts['00000000-0000-0000-0000-000000000000'] = counts['00000000-0000-0000-0000-000000000000'] + noneCount;
    }

    return counts;
  }, [clientVendorRelationships, normalizedClients]);

  return (
    <>
      <Box className={classes.cardContainer}>
        <Box className={classes.header}>
          <Box display="flex" alignItems="center" gap={12}>
            <FontAwesomeIcon icon={['fas', 'handshake']} size="2x" />
            <Typography variant="h5" style={{ marginLeft: '12px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
              Client Relationships
            </Typography>
          </Box>

          <Button variant="contained" color="primary" onClick={openModal}>
            View
          </Button>
        </Box>
        <Box>
          {CLIENT_VENDOR_RELATIONSHIPS.map(r => (
            <Box key={r.id} display="flex" justifyContent="space-between">
              <Typography variant="body1">{r.label}</Typography>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {relationshipCounts[r.id] || 0}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Modal open={modalOpen} onClose={closeModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 4,
            boxShadow: 24,
            width: 1000,
            maxHeight: '80vh',
            overflow: 'auto'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Client Vendor Relationship Assignment</Typography>
            <Button onClick={closeModal}>
              Close
            </Button>
          </Box>
          <TextField
            placeholder="Search clients..."
            variant="outlined"
            size="small"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={['fas', 'search']} size="sm" />
                </InputAdornment>
              )
            }}
            sx={{ mb: 2, width: '100%' }}
          />
          <Grid container>
            <Grid item xs={2}>
              <Box sx={{ padding: '5px' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Client
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ padding: '5px' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Client Discount Rate
                </Typography>
              </Box>
            </Grid>
            {CLIENT_VENDOR_RELATIONSHIPS.map(r => (
              <Grid item xs key={r.id}>
                <Box sx={{ padding: '5px' }}>
                  <Typography variant="subtitle1" fontWeight="bold" align="center">
                    {r.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          {filteredClients.map(client => {
            const clientKey = String(client.clientRecordId ?? client.clientId ?? '');
            const selected = clientVendorRelationshipForm[clientKey] || relationshipLookupFromApi[clientKey] || {
              relationshipId: CLIENT_VENDOR_RELATIONSHIPS[3].id,
              clientDiscountRate: null
            };
            return (
              <Grid container key={clientKey || client.shortName} alignItems="center">
                <Grid item xs={2}>
                  <Box sx={{ padding: '5px' }}>{client.shortName}</Box>
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    variant="standard"
                    value={selected.clientDiscountRate ?? client.clientDiscountRate ?? ''}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      disableUnderline: false
                    }}
                    type="number"
                    inputProps={{
                      style: { fontSize: 12, padding: '6px 0' }
                    }}
                    onWheel={e => {
                      e.target.blur();
                    }}
                    sx={{
                      width: '100%',
                      mb: 0,
                      '& .MuiInput-underline:before': { borderBottomColor: '#e0e0e0' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#bdbdbd' },
                      '& .MuiInput-underline:after': { borderBottomColor: '#3f51b5' }
                    }}
                  />
                </Grid>
                {CLIENT_VENDOR_RELATIONSHIPS.map(r => {
                  const isSelected = isIdEqual(selected.relationshipId, r.id);
                  return (
                    <Grid item xs key={r.id}>
                      <Box
                        sx={{
                          height: 40,
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        {isSelected && <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            );
          })}
          <Box display="flex" justifyContent="space-between" mt={3}>
            <Typography variant="body2" color="textSecondary">
              {filteredClients.length} client(s)
            </Typography>
            <Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

ClientVendorRelationshipGrid.propTypes = {
  classes: PropTypes.object.isRequired,
  clients: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  clientVendorRelationships: PropTypes.array,
  theme: PropTypes.object
};

export default withStyles(styles, { withTheme: true })(ClientVendorRelationshipGrid);
